import React, { useState, useEffect, useMemo, useRef } from "react";
import { Icon } from "@iconify/react";
import AuthShell, { canAutoFocus } from "./AuthShell";
import {
  AuthField,
  FormAlert,
  PasswordField,
  SubmitLabel,
} from "./AuthField";
import s from "./Auth.module.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { getTranferBankList } from "../../store/slices/user";
import { useAuth } from "../../contexts/JWTContext";
import { useNavigate } from "react-router-dom";

const STEPS = ["ข้อมูลผู้ใช้งาน", "ข้อมูลบัญชี", "ตรวจสอบข้อมูล"];
const TRUE_WALLET = { bankCode: "true", bankName: "ทรูมันนี่ วอลเล็ท" };

const bankIcons = require.context("../../assets/bankicon", false, /\.webp$/);
const bankLogo = (code) => {
  const key = `./${code}.webp`;
  return bankIcons.keys().includes(key) ? bankIcons(key) : null;
};

function BankLogo({ code }) {
  const src = bankLogo(code);
  if (src) return <img src={src} alt="" className={s.bankLogo} />;
  return (
    <span className={s.bankLogoEmpty}>
      <Icon icon="solar:card-2-bold-duotone" width={20} />
    </span>
  );
}

function StepList({ step }) {
  return (
    <ol className={s.stepList}>
      {STEPS.map((label, i) => (
        <li
          key={label}
          className={`${s.stepItem} ${i === step ? s.stepNow : ""} ${
            i < step ? s.stepDone : ""
          }`}
          aria-current={i === step ? "step" : undefined}
        >
          <span className={s.stepDot}>
            {i < step ? <Icon icon="ic:round-check" width={18} /> : i + 1}
          </span>
          <span className={s.stepLabel}>{label}</span>
        </li>
      ))}
    </ol>
  );
}

export default function RegisterModal({ onClose, onSwitchToLogin, hideClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { transferBankList } = useSelector((state) => state.user);
  const { login } = useAuth();
  const [step, setStep] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [pickingBank, setPickingBank] = useState(true);
  const [needsManualLogin, setNeedsManualLogin] = useState(false);
  const reviewOpenedAt = useRef(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBankList, setShowBankList] = useState([]);

  const [form, setForm] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    bank_name: "",
    book_number: "",
    book_name: "",
  });

  useEffect(() => {
    dispatch(getTranferBankList());
  }, [dispatch]);

  useEffect(() => {
    setShowBankList(transferBankList);
  }, [transferBankList]);

  // read inviter and campaign from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const inviterID = urlParams.get("friend") || 0;
  const registerFrom = urlParams.get("campaign") || 0;

  const checkDuplicate = async (
    username,
    book_number,
    bank_name,
    book_name,
  ) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/usersystem/checkregister`,
        {
          username,
          book_number,
          bank_name,
          book_name,
        },
      );
      if (res.data === "duplicatebooknumberandusername") {
        throw new Error("ชื่อผู้ใช้งานกับบัญชีธนาคารซ้ำในระบบ!");
      } else if (res.data === "duplicatebooknumber") {
        throw new Error("บัญชีธนาคารซ้ำในระบบ!");
      } else if (res.data === "duplicateusername") {
        throw new Error("ชื่อผู้ใช้งานซ้ำในระบบ!");
      } else if (res.data === "duplicatebookname") {
        throw new Error("ชื่อบัญชีซ้ำในระบบ!");
      }
      return true;
    } catch (err) {
      throw err;
    }
  };

  const submitRegistration = async (values) => {
    try {
      const payload = {
        username: values.username,
        password: values.password,
        book_number: values.book_number,
        bank_name: values.bank_name,
        book_name: values.book_name,
        inviterID: inviterID,
        registerFrom: registerFrom,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/usersystem`,
        payload,
      );
      return res.data;
    } catch (err) {
      throw err;
    }
  };

  const validateStep0 = () => {
    const { username, password, confirmPassword } = form;
    if (!/^[0-9]{10}$/.test(username)) {
      return { field: "username", msg: "กรุณากรอกเบอร์โทรศัพท์ 10 หลัก" };
    }
    if (!password || password.length < 6) {
      return { field: "password", msg: "กรุณากรอกรหัสผ่านอย่างน้อย 6 ตัวอักษร" };
    }
    if (password !== confirmPassword) {
      return { field: "confirmPassword", msg: "รหัสผ่านไม่ตรงกัน" };
    }
    return null;
  };

  const validateStep1 = () => {
    const { bank_name, book_number, book_name } = form;
    if (!bank_name) {
      return { field: "bank_name", msg: "กรุณาเลือกธนาคารหรือทรูวอลเล็ต" };
    }
    if (!book_number) {
      return { field: "book_number", msg: "กรุณากรอกเลขบัญชี/เบอร์ทรูวอลเล็ต" };
    }
    if (!book_name) return { field: "book_name", msg: "กรุณากรอกชื่อบัญชี" };
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const digitsOnly = name === "username" || name === "book_number";
    let next = digitsOnly ? value.replace(/\D/g, "") : value;
    if (name === "username") next = next.slice(0, 10);
    setForm((f) => ({ ...f, [name]: next }));
    setErrorMsg(null);
    setFieldError(null);
  };

  const pickBank = (bankCode) => {
    setForm((f) => ({ ...f, bank_name: bankCode }));
    setPickingBank(false);
    setErrorMsg(null);
    setFieldError(null);
  };

  const goToStep = (next) => {
    setErrorMsg(null);
    setFieldError(null);
    setStep(next);
  };

  const handleNext = () => {
    const err = step === 0 ? validateStep0() : validateStep1();
    if (err) return setFieldError(err);
    if (step === 1) reviewOpenedAt.current = Date.now();
    goToStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    setErrorMsg(null);
    if (step < 2) return handleNext();

    // a double-click on "ต่อไป" must not skip the review step
    if (Date.now() - reviewOpenedAt.current < 500) return;

    // final submit
    setIsSubmitting(true);
    try {
      await checkDuplicate(
        form.username,
        form.book_number,
        form.bank_name,
        form.book_name,
      );
      await submitRegistration(form);

      // Auto login after successful registration
      try {
        await login(form.username, form.password);
        onClose();
        navigate("/home", { replace: true });
      } catch (loginErr) {
        console.error("Auto login failed:", loginErr);
        setNeedsManualLogin(true);
      }
    } catch (err) {
      const body = err?.response?.data;
      setErrorMsg(
        (typeof body === "string" && body) ||
          body?.message ||
          err.message ||
          "สมัครสมาชิกไม่สำเร็จ",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const banks = useMemo(
    () => [TRUE_WALLET, ...(showBankList || [])],
    [showBankList],
  );
  const selectedBank = banks.find((b) => b.bankCode === form.bank_name);
  const isTrueWallet = form.bank_name === TRUE_WALLET.bankCode;
  const errorFor = (field) =>
    fieldError?.field === field ? fieldError.msg : undefined;

  const isDirty = Boolean(form.username || form.password || form.bank_name);
  const passwordsMatch =
    form.confirmPassword.length > 0 && form.password === form.confirmPassword;

  const aside = (
    <>
      <span className="lux-eyebrow">Join the club</span>
      <p className={s.asideTitle}>
        สมัครง่าย
        <br />
        <b className="lux-gold-text">3 ขั้นตอน</b>
      </p>
      <StepList step={needsManualLogin ? STEPS.length : step} />
    </>
  );

  if (needsManualLogin) {
    return (
      <AuthShell
        mode="register"
        onSwitchMode={onSwitchToLogin}
        onClose={onClose}
        onDismiss={onClose}
        hideClose={hideClose}
        aside={aside}
      >
        <div className={s.done}>
          <span className={s.doneIcon}>
            <Icon icon="ic:round-check" width={38} />
          </span>
          <strong>สมัครสมาชิกสำเร็จ</strong>
          <p>กรุณาเข้าสู่ระบบด้วยเบอร์โทรศัพท์และรหัสผ่านที่ตั้งไว้</p>
          <button
            type="button"
            className="lux-btn lux-btn--gold lux-btn--lg lux-btn--block"
            onClick={onSwitchToLogin}
          >
            <span>ไปหน้าเข้าสู่ระบบ</span>
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      mode="register"
      onSwitchMode={onSwitchToLogin}
      onClose={onClose}
      onDismiss={isDirty ? undefined : onClose}
      hideClose={hideClose}
      aside={aside}
    >
      <div className={s.mobileSteps}>
        <StepList step={step} />
      </div>

      <form className={s.form} onSubmit={handleSubmit} noValidate>
        {step === 0 && (
          <div key="account" className={s.stepPanel}>
            <AuthField
              label="เบอร์โทรศัพท์"
              icon="solar:phone-calling-rounded-linear"
              type="tel"
              inputMode="numeric"
              name="username"
              autoComplete="tel-national"
              placeholder="0812345678"
              value={form.username}
              onChange={handleChange}
              error={errorFor("username")}
              hint="ใช้เป็นชื่อผู้ใช้สำหรับเข้าสู่ระบบ"
              autoFocus={canAutoFocus()}
            />
            <PasswordField
              label="รหัสผ่าน"
              icon="solar:lock-keyhole-minimalistic-linear"
              name="password"
              autoComplete="new-password"
              placeholder="อย่างน้อย 6 ตัวอักษร"
              value={form.password}
              onChange={handleChange}
              error={errorFor("password")}
            />
            <PasswordField
              label="ยืนยันรหัสผ่าน"
              icon="solar:shield-check-linear"
              name="confirmPassword"
              autoComplete="new-password"
              placeholder="กรอกรหัสผ่านอีกครั้ง"
              value={form.confirmPassword}
              onChange={handleChange}
              error={errorFor("confirmPassword")}
              hint={passwordsMatch ? "รหัสผ่านตรงกัน" : undefined}
            />
          </div>
        )}

        {step === 1 && (
          <div key="bank" className={s.stepPanel}>
            <div className={`${s.field} ${errorFor("bank_name") ? s.fieldBad : ""}`}>
              <span className={s.label}>ธนาคารรับเงิน</span>

              {pickingBank || !selectedBank ? (
                <div className={s.bankGrid} role="radiogroup" aria-label="เลือกธนาคาร">
                  {banks.map((bank) => (
                    <button
                      key={bank.bankCode}
                      type="button"
                      role="radio"
                      aria-checked={form.bank_name === bank.bankCode}
                      className={`${s.bank} ${
                        form.bank_name === bank.bankCode ? s.bankOn : ""
                      }`}
                      onClick={() => pickBank(bank.bankCode)}
                    >
                      <BankLogo code={bank.bankCode} />
                      <span>{bank.bankName}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className={s.bankPicked}>
                  <BankLogo code={selectedBank.bankCode} />
                  <strong>{selectedBank.bankName}</strong>
                  <button
                    type="button"
                    className={s.textBtn}
                    onClick={() => setPickingBank(true)}
                  >
                    เปลี่ยน
                  </button>
                </div>
              )}

              {errorFor("bank_name") && (
                <div className={s.msgBad}>{errorFor("bank_name")}</div>
              )}
            </div>

            {selectedBank && !pickingBank && (
              <>
                <AuthField
                  label={isTrueWallet ? "เบอร์ทรูวอลเล็ต" : "เลขบัญชี"}
                  icon="solar:card-2-linear"
                  type="text"
                  inputMode="numeric"
                  name="book_number"
                  autoComplete="off"
                  placeholder={isTrueWallet ? "กรอกเบอร์ทรูวอลเล็ต" : "กรอกเลขบัญชี"}
                  value={form.book_number}
                  onChange={handleChange}
                  error={errorFor("book_number")}
                  autoFocus={canAutoFocus()}
                />
                <AuthField
                  label="ชื่อบัญชี"
                  icon="solar:user-rounded-linear"
                  type="text"
                  name="book_name"
                  autoComplete="name"
                  placeholder="ชื่อ-นามสกุล ตามบัญชี"
                  value={form.book_name}
                  onChange={handleChange}
                  error={errorFor("book_name")}
                />
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div key="review" className={s.stepPanel}>
            <section className={s.review}>
              <header>
                <span>ข้อมูลผู้ใช้งาน</span>
                <button type="button" className={s.textBtn} onClick={() => goToStep(0)}>
                  แก้ไข
                </button>
              </header>
              <dl>
                <div>
                  <dt>เบอร์โทรศัพท์</dt>
                  <dd>{form.username}</dd>
                </div>
              </dl>
            </section>

            <section className={s.review}>
              <header>
                <span>บัญชีรับเงิน</span>
                <button type="button" className={s.textBtn} onClick={() => goToStep(1)}>
                  แก้ไข
                </button>
              </header>
              <dl>
                <div>
                  <dt>ธนาคาร</dt>
                  <dd className={s.reviewBank}>
                    <BankLogo code={form.bank_name} />
                    {selectedBank?.bankName || form.bank_name}
                  </dd>
                </div>
                <div>
                  <dt>{isTrueWallet ? "เบอร์ทรูวอลเล็ต" : "เลขบัญชี"}</dt>
                  <dd>{form.book_number}</dd>
                </div>
                <div>
                  <dt>ชื่อบัญชี</dt>
                  <dd>{form.book_name}</dd>
                </div>
              </dl>
            </section>
          </div>
        )}

        <FormAlert>{errorMsg && String(errorMsg)}</FormAlert>

        <div className={s.actions}>
          {step > 0 && (
            <button
              type="button"
              className={`lux-btn lux-btn--ghost lux-btn--lg ${s.backBtn}`}
              onClick={() => goToStep(step - 1)}
              disabled={isSubmitting}
            >
              <Icon icon="solar:alt-arrow-left-linear" width={18} />
              <span>ย้อนกลับ</span>
            </button>
          )}
          <button
            type="submit"
            className={`lux-btn lux-btn--gold lux-btn--lg ${s.nextBtn}`}
            disabled={isSubmitting}
          >
            <SubmitLabel loading={isSubmitting} loadingText="กำลังสมัคร...">
              <span>{step === 2 ? "ยืนยันการสมัคร" : "ต่อไป"}</span>
              {step < 2 && <Icon icon="solar:alt-arrow-right-linear" width={18} />}
            </SubmitLabel>
          </button>
        </div>
      </form>
    </AuthShell>
  );
}
